<template>
  <div class="page-container">
    <div class="page-content">
      <div class="page-header">
        <div class="d-flex align-items-center mb-3">
          <h1 class="text-white">
            <i class="mdi mdi-file-chart"></i>
            Exportação Quiver
          </h1>
          <div class="d-flex gap-2 ml-auto align-items-center">
            <button
              :disabled="loadingExport || !hasResult"
              @click="exportQuiver()"
              class="btn btn-outline-white"
            >
              <i v-if="!loadingExport" class="fa fa-file-excel"></i>
              <b-spinner v-else small></b-spinner>
              <span class="d-none d-md-inline-block ml-2">Exportar Quiver</span>
            </button>
          </div>
        </div>
      </div>

      <div
        v-if="loading"
        class="overlay-modal d-flex align-items-center justify-content-center"
      >
        <div>
          <div class="d-flex justify-content-center mb-2">
            <b-spinner label="Loading..."></b-spinner>
          </div>
          <div class="d-flex align-items-center">
            <strong>Carregando</strong>
          </div>
        </div>
      </div>

      <div class="page-body">
        <!-- Filtros -->
        <div class="card bg-light-gray mb-3">
          <div class="card-body">
            <form @submit.prevent="search">
              <div class="row">
                <div class="col-12 col-lg-3 mb-3">
                  <label>Empresa</label>
                  <b-form-select
                    v-model="filters.company"
                    :options="companyOptions"
                    size="lg"
                  ></b-form-select>
                </div>
                <div class="col-12 col-lg-3 mb-3">
                  <label>Tipo de relatório</label>
                  <b-form-select
                    v-model="filters.type"
                    :options="typeOptions"
                    size="lg"
                  ></b-form-select>
                </div>
                <div class="col-6 col-lg-3 mb-3">
                  <label>Período (início)</label>
                  <b-form-input
                    type="date"
                    v-model="filters.start_date"
                    size="lg"
                  ></b-form-input>
                </div>
                <div class="col-6 col-lg-3 mb-3">
                  <label>Período (fim)</label>
                  <b-form-input
                    type="date"
                    v-model="filters.end_date"
                    size="lg"
                  ></b-form-input>
                </div>
                <div class="col-12 col-lg-4 mb-3">
                  <label>Buscar por Nº FINN</label>
                  <b-form-input
                    v-model="filters.search"
                    placeholder="Digite o Nº FINN"
                    size="lg"
                  ></b-form-input>
                </div>
              </div>
              <div class="d-flex align-items-center justify-content-between flex-wrap">
                <small class="text-muted">
                  Os status e demais regras do relatório são pré-definidos conforme o
                  template do Quiver. O período vem pré-preenchido da última sexta à
                  última quinta-feira, mas pode ser ajustado.
                </small>
                <div class="d-flex gap-2 mt-2">
                  <button
                    type="button"
                    class="btn btn-outline-secondary"
                    :disabled="loading"
                    @click="cleanFilter"
                  >
                    <i class="fa fa-times"></i>
                    <span class="ml-2">Limpar filtros</span>
                  </button>
                  <button type="submit" class="btn btn-primary" :disabled="loading">
                    <i class="fa fa-search"></i>
                    <span class="ml-2">Buscar prévia</span>
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>

        <!-- Prévia -->
        <div class="card bg-light-gray">
          <div class="card-body pt-1">
            <div class="d-flex align-items-center flex-wrap my-2">
              <strong>{{ total }}</strong>
              <span class="ml-1">registro(s) encontrado(s)</span>
              <small v-if="filters.search" class="text-muted ml-2">
                — filtrando por Nº FINN "{{ filters.search }}"
              </small>
            </div>

            <div class="table-responsive reports-table">
              <table role="table" class="table-v3">
                <thead role="rowgroup">
                  <tr>
                    <th
                      v-for="(col, idx) in previewColumns"
                      :key="idx"
                      class="nowrap"
                    >
                      {{ col.label }}
                    </th>
                    <th class="nowrap text-center">CORRETORA</th>
                    <th class="nowrap text-center actions-col">AÇÕES</th>
                  </tr>
                </thead>
                <tbody role="rowgroup">
                  <tr v-for="(row, rIndex) in rows" :key="rIndex">
                    <td
                      v-for="(col, cIndex) in previewColumns"
                      :key="cIndex"
                      class="report-cell"
                      :title="cellValue(row, col.name)"
                    >
                      {{ cellValue(row, col.name) }}
                    </td>
                    <td class="text-center report-cell" :title="cellValue(row, 'Corretora')">
                      {{ cellValue(row, 'Corretora') }}
                    </td>
                    <td class="text-center actions-col">
                      <button
                        class="btn btn-sm btn-outline-primary nowrap"
                        @click="openBrokerModal(row)"
                        title="Trocar corretora"
                      >
                        <i class="mdi mdi-swap-horizontal"></i>
                        <span class="d-none d-xl-inline ml-1">Trocar</span>
                      </button>
                    </td>
                  </tr>
                  <tr v-if="!rows.length">
                    <td :colspan="previewColumns.length + 2" class="text-center py-4">
                      Nenhum registro. Ajuste os filtros e clique em "Buscar prévia".
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <b-pagination
              v-if="total > perPage"
              v-model="currentPage"
              :total-rows="total"
              :per-page="perPage"
              align="center"
              class="my-3"
              @change="onPageChange"
            ></b-pagination>
          </div>
        </div>
      </div>
    </div>

    <!-- Trocar corretora -->
    <b-modal
      ref="brokerModal"
      id="modal-broker"
      title="Trocar corretora"
      centered
      :ok-disabled="savingBroker"
      ok-title="Confirmar troca"
      cancel-title="Cancelar"
      @ok.prevent="confirmBroker"
    >
      <p class="mb-2">
        <strong>Segurado:</strong> {{ selectedRowInfo.insured }}<br />
        <strong>Nº Apólice:</strong> {{ selectedRowInfo.policy }}<br />
        <strong>Corretora atual:</strong> {{ selectedRowInfo.broker || '—' }}
      </p>
      <label>Mover para</label>
      <b-form-select
        v-model="targetBroker"
        :options="brokerNameOptions"
        size="lg"
      ></b-form-select>
    </b-modal>
  </div>
</template>

<script>
import moment from "moment";
import fileDownload from "js-file-download";
import reportsService from "@/services/reports";
import messageAlert from "@/mixins/messageAlert";

const PREVIEW_COLUMNS = {
  apolices: [
    { name: "N. FINN", label: "Nº FINN" },
    { name: "Nome do Segurado", label: "SEGURADO" },
    { name: "Seguradora", label: "SEGURADORA" },
    { name: "Início da Vigência", label: "INÍCIO VIGÊNCIA" },
    { name: "Término da Vigência", label: "FIM VIGÊNCIA" },
    { name: "Valor do Prêmio Total", label: "PRÊMIO" },
    { name: "Status", label: "STATUS" },
  ],
  endossos: [
    { name: "N. FINN", label: "Nº FINN" },
    { name: "Nome do Segurado", label: "SEGURADO" },
    { name: "Seguradora", label: "SEGURADORA" },
    { name: "Tipo Endosso", label: "TIPO ENDOSSO" },
    { name: "Status Endosso", label: "STATUS ENDOSSO" },
    { name: "Descrição Endosso", label: "DESCRIÇÃO" },
    { name: "Endosso última atualização", label: "ATUALIZADO EM" },
  ],
};

export default {
  name: "list-reports",
  mixins: [messageAlert],
  data: () => ({
    loading: false,
    loadingExport: false,
    filters: {
      company: "finn",
      type: "apolices",
      start_date: "",
      end_date: "",
      search: "",
    },
    companyOptions: [
      { value: "finn", text: "Finn" },
      { value: "acrisure", text: "Acrisure" },
      { value: "outros", text: "Outros" },
    ],
    typeOptions: [
      { value: "apolices", text: "Apólices" },
      { value: "endossos", text: "Endossos" },
    ],
    columns: [],
    rows: [],
    total: 0,
    currentPage: 1,
    perPage: 20,
    // tipo dos resultados atualmente carregados (para escolher as colunas de prévia)
    loadedType: "apolices",
    // troca de corretora
    selectedRow: null,
    targetBroker: "Finn Seguros",
    savingBroker: false,
    brokerNameOptions: [
      { value: "Finn Seguros", text: "Finn Seguros" },
      { value: "Acrisure", text: "Acrisure" },
      { value: "ItsSeg Corretora de Seguros", text: "ItsSeg Corretora de Seguros" },
    ],
  }),
  computed: {
    hasResult() {
      return this.total > 0;
    },
    previewColumns() {
      return PREVIEW_COLUMNS[this.loadedType] || PREVIEW_COLUMNS.apolices;
    },
    selectedRowInfo() {
      if (!this.selectedRow) {
        return { insured: "", policy: "", broker: "" };
      }
      return {
        insured: this.cellValue(this.selectedRow, "Nome do Segurado"),
        policy: this.cellValue(this.selectedRow, "N° da Apólice"),
        broker: this.cellValue(this.selectedRow, "Corretora"),
      };
    },
  },
  created() {
    this.setDefaultPeriod();
  },
  methods: {
    // Última sexta → última quinta-feira (semana fechada anterior)
    setDefaultPeriod() {
      let end = moment().startOf("day");
      do {
        end.subtract(1, "day");
      } while (end.isoWeekday() !== 4); // 4 = quinta-feira
      const start = end.clone().subtract(6, "days"); // sexta anterior
      this.filters.start_date = start.format("YYYY-MM-DD");
      this.filters.end_date = end.format("YYYY-MM-DD");
    },
    cellValue(row, columnName) {
      const idx = this.columns.indexOf(columnName);
      return idx >= 0 ? row[idx] : "";
    },
    // Aplicar filtros/busca volta para a primeira página
    search() {
      this.currentPage = 1;
      this.fetchPage();
    },
    // Volta ao default: Finn, Apólices, última sexta → última quinta, sem busca
    cleanFilter() {
      this.filters.company = "finn";
      this.filters.type = "apolices";
      this.filters.search = "";
      this.setDefaultPeriod();
      this.search();
    },
    onPageChange(page) {
      this.currentPage = page;
      this.fetchPage();
    },
    fetchPage() {
      this.loading = true;
      reportsService
        .preview(this.buildParams())
        .then((response) => {
          this.columns = response.data.columns || [];
          this.rows = response.data.rows || [];
          this.total = response.data.total || 0;
          this.loadedType = this.filters.type;
        })
        .catch(() => {
          this.$bvToast.toast("Não foi possível carregar a prévia do relatório.", {
            title: "Erro",
            variant: "danger",
            solid: true,
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    exportQuiver() {
      this.loadingExport = true;
      reportsService
        .download(this.baseParams())
        .then((response) => {
          const filename = `quiver-${this.filters.type}-${this.filters.company}-${this.filters.start_date}_${this.filters.end_date}.xlsx`;
          fileDownload(response.data, filename);
        })
        .catch(() => {
          this.$bvToast.toast("Não foi possível gerar o arquivo do Quiver.", {
            title: "Erro",
            variant: "danger",
            solid: true,
          });
        })
        .finally(() => {
          this.loadingExport = false;
        });
    },
    // Filtros base do relatório (usados na exportação — sem paginação/busca)
    baseParams() {
      return {
        type: this.filters.type,
        company: this.filters.company,
        start_date: this.filters.start_date,
        end_date: this.filters.end_date,
      };
    },
    // Parâmetros da prévia: base + paginação + busca por Nº FINN
    buildParams() {
      return {
        ...this.baseParams(),
        page: this.currentPage,
        per_page: this.perPage,
        search: this.filters.search || undefined,
      };
    },
    openBrokerModal(row) {
      this.selectedRow = row;
      // sugere a corretora oposta à do filtro atual
      this.targetBroker =
        this.filters.company === "finn" ? "Acrisure" : "Finn Seguros";
      this.$refs.brokerModal.show();
    },
    confirmBroker() {
      const policyId = this.cellValue(this.selectedRow, "id");
      if (!policyId) {
        return;
      }
      this.savingBroker = true;
      reportsService
        .updateBroker(policyId, this.targetBroker)
        .then(() => {
          this.$bvToast.toast("Corretora alterada com sucesso.", {
            title: "Sucesso",
            variant: "success",
          });
          this.$refs.brokerModal.hide();
          this.fetchPage();
        })
        .catch(() => {
          this.$bvToast.toast("Não foi possível trocar a corretora.", {
            title: "Erro",
            variant: "danger",
            solid: true,
          });
        })
        .finally(() => {
          this.savingBroker = false;
        });
    },
  },
};
</script>

<style scoped>
/* Caixa com altura limitada: os dois scrolls (horizontal e vertical) ficam
   DENTRO dessa área, então a barra horizontal fica sempre visível/alcançável
   mesmo com muitos registros (antes ela ia parar centenas de linhas abaixo). */
.reports-table {
  max-height: 60vh;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
}

/* Cabeçalho fixo ao rolar verticalmente dentro da caixa */
.reports-table table.table-v3 thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  background-color: #edf4fa; /* mesma cor do card (bg-light-gray) */
}

/* Trunca textos longos (ex.: nome do segurado) com "..."; o valor completo
   fica disponível no title (hover). Evita que um nome grande estoure a coluna. */
.reports-table .report-cell {
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

@media (max-width: 575.98px) {
  .reports-table .report-cell {
    max-width: 140px;
  }
}
</style>

<!-- Estilo global (não-scoped): os toasts são renderizados fora do componente
     (portal no body), então o scoped não os alcança. Fonte branca nos toasts de
     erro (variante danger sólida) para legibilidade sobre o fundo vermelho. -->
<style>
.b-toast-danger.b-toast-solid .toast,
.b-toast-danger.b-toast-solid .toast .toast-header,
.b-toast-danger.b-toast-solid .toast .toast-body {
  color: #fff !important;
}

.b-toast-danger.b-toast-solid .toast .close {
  color: #fff !important;
  opacity: 0.9;
}
</style>
